import { getDemographicCatalog } from "common";
import { findIndex, isNumber, sortBy, uniq } from "lodash";
import {
    AdjustmentOperation,
    AdjustmentType,
    AggregatorObject,
    CatalogItem,
    CouponAlgorithm,
    CouponScope,
    DemographicPrice,
    Experience,
    LineItemCode,
    LineItemTemplate,
    LineItemType,
    PartnerDemographicPrice,
    PartnerDemographicTerm,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleActionObject,
    PurchaseRuleFilterObject,
    PurchaseRuleLink,
    PurchaseRuleModifier,
    PurchaseRuleModifierOperation,
    PurchaseRuleShortcut,
    PurchaseRuleShortcutDiscountOption,
    PurchaseRuleShortcutTemplateCode,
    PurchaseRuleShortcutType,
    Seller,
    TagId,
    TermsType,
} from "types";
import { SelectionState } from "../hooks/useSelection";
import { getCatalogItemByTemplateId } from "./Catalog";
import {
    filterPurchaseRulesByProductId,
    getEqualFilter,
    getProductIdsFromPurchaseRule,
    getProductIdsFromPurchaseRules,
    getPurchaseRulesByTag,
    getReduceAggregator,
    getReturnValueAggregator,
    getTagIdsFromPurchaseRule,
} from "./PurchaseRule";

/**
 * Get selected demographics from terms template purchase rule.
 */
export const getDemographicSelection = (
    termsTemplate: PurchaseRule,
    demographicTemplates?: LineItemTemplate[],
): SelectionState => {
    const [action] = termsTemplate.actions;

    if (action.shortcut) {
        const options = action.shortcut?.options as PurchaseRuleShortcutDiscountOption;
        if ("templateIds" in options) {
            const templateIds = options.templateIds!;
            return {
                all: false,
                // The API merges the Fixed Price template id into templateIds, but it is not a
                // demographic the seller picked, so it must not surface as one.
                selected: demographicTemplates
                    ? templateIds.filter((templateId) =>
                          demographicTemplates.some(
                              (demographicTemplate) =>
                                  demographicTemplate.id === templateId &&
                                  demographicTemplate.code !== LineItemCode.PER_OUTING_PRICE,
                          ),
                      )
                    : templateIds,
            };
        }

        if ("templateCodes" in options) {
            const [templateCode] = options.templateCodes!;

            if (templateCode === "demographic_subtotal" || templateCode === "unit_subtotal") {
                return { all: true, selected: [] };
            }
        }

        if ("templateTypes" in options) {
            return { all: true, selected: [] };
        }
    }

    return { all: false, selected: [] };
};

/**
 * Equipment counterpart of getDemographicSelection: the two share one rule, so each resolves its own
 * unit type out of the same templateIds.
 */
export const getEquipmentSelection = (termsTemplate: PurchaseRule, equipmentTemplateIds?: ID[]): SelectionState => {
    const [action] = termsTemplate.actions;

    if (action.shortcut) {
        const options = action.shortcut?.options as PurchaseRuleShortcutDiscountOption;
        if ("templateIds" in options) {
            const templateIds = options.templateIds!;
            return {
                all: false,
                selected: equipmentTemplateIds
                    ? templateIds.filter((templateId) => equipmentTemplateIds.includes(templateId))
                    : [],
            };
        }

        if ("templateTypes" in options) {
            return { all: options.templateTypes!.includes(LineItemType.EQUIPMENT), selected: [] };
        }
    }

    return { all: false, selected: [] };
};

/**
 * Get selected products from terms template purchase rule.
 */
export const getProductSelection = (purchaseRule: PurchaseRule): SelectionState => {
    const selected: string[] = getProductIdsFromPurchaseRule(purchaseRule);
    return { all: selected.length === 0, selected: selected };
};

/**
 * Get selected products from terms template purchase rules for Advanced.
 */
export const getProductSelectionFromPurchaseRules = (purchaseRules: PurchaseRule[]): SelectionState => {
    const selected: string[] = getProductIdsFromPurchaseRules(purchaseRules);
    return { all: selected.length === 0, selected: sortBy(uniq(selected)) };
};

const addonTemplateCodes = new Set([
    PurchaseRuleShortcutTemplateCode.UNIT,
    PurchaseRuleShortcutTemplateCode.ADDON,
    PurchaseRuleShortcutTemplateCode.PICKUP_SUBTOTAL,
]);

const addonTemplateTypes = new Set([LineItemType.ADDON, LineItemType.PICKUP]);

/**
 * Check if add-ons are included in terms template purchase rule.
 *
 * A shortcut can carry templateCodes and templateTypes at the same time, so neither may
 * short-circuit the other.
 */
export const getIncludesAddOns = (termsTemplate: PurchaseRule): boolean => {
    const [action] = termsTemplate.actions;
    const options = action.shortcut?.options as PurchaseRuleShortcutDiscountOption | undefined;

    const hasAddOnCode = (options?.templateCodes ?? []).some((templateCode) =>
        addonTemplateCodes.has(templateCode as PurchaseRuleShortcutTemplateCode),
    );
    const hasAddOnType = (options?.templateTypes ?? []).some((templateType) =>
        addonTemplateTypes.has(templateType as LineItemType),
    );

    return hasAddOnCode || hasAddOnType;
};

export const getIncludeCustomLineItems = (termsTemplate: PurchaseRule): boolean => {
    const [action] = termsTemplate.actions;
    const includeCustomLineItemTypes = new Set([
        LineItemType.ADJUSTMENT,
        LineItemType.TAXED_ADJUSTMENT,
        LineItemType.FEEABLE_TAXABLE_ADJUSTMENT,
        LineItemType.AFFILIATE_DISCOUNT,
    ]);

    if (action.shortcut) {
        const options = action.shortcut?.options as PurchaseRuleShortcutDiscountOption;

        if ("templateTypes" in options) {
            return options.templateTypes!.some((templateType) =>
                includeCustomLineItemTypes.has(templateType as LineItemType),
            );
        }
    }

    return false;
};

export const initializeTermsTemplatePurchaseRule = (
    seller: Seller,
    lineItemTemplate: LineItemTemplate,
): PurchaseRule => {
    const typeText = lineItemTemplate.type === LineItemType.AFFILIATE_DISCOUNT ? "Discount" : "Commission";

    return {
        name: `${typeText} for ${lineItemTemplate.name}`,
        seller: { id: seller.id },
        actions: [
            {
                template: { id: lineItemTemplate.id },
                object: PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION,
                shortcut: {
                    type: PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT,
                    amount: null as any,
                    options: { templateCodes: ["demographic_subtotal"] },
                },
            },
        ],
        tags: [{ id: "Affiliate" }, { id: lineItemTemplate.type }],
    };
};

export const getScope = (termsTemplate: PurchaseRule, equipmentTemplateIds?: Set<ID>): CouponScope => {
    const [action] = termsTemplate.actions;

    if (action.shortcut!.type !== PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT) {
        return CouponScope.PER_BOOKING;
    }

    // Per-person and per-equipment share a shortcut type, so the unit type it targets is what separates
    // them: an "all" rule carries templateTypes, a narrowed one only ids the caller must resolve.
    const options = action.shortcut?.options as PurchaseRuleShortcutDiscountOption | undefined;
    if (options?.templateTypes?.includes(LineItemType.EQUIPMENT)) {
        return CouponScope.PER_EQUIPMENT;
    }

    return equipmentTemplateIds && options?.templateIds?.some((id) => equipmentTemplateIds.has(id))
        ? CouponScope.PER_EQUIPMENT
        : CouponScope.PER_PERSON;
};

export const getAlgorithm = (termsTemplate: PurchaseRule): CouponAlgorithm => {
    const [action] = termsTemplate.actions;

    return action.shortcut!.type === PurchaseRuleShortcutType.PERCENT
        ? CouponAlgorithm.PERCENT
        : CouponAlgorithm.ABSOLUTE;
};

export const getAmount = (termsTemplate: PurchaseRule): number => {
    const [action] = termsTemplate.actions;

    if (action.shortcut) {
        // The discount amount is being recorded as a negative value, inverting to use to populate the form
        return action.shortcut.amount >= 0 ? Number(action.shortcut.amount) : action.shortcut.amount * -1;
    }

    return 0;
};

export const getTermsType = (termsTemplate?: PurchaseRuleLink): TermsType => {
    const isAdvanced = termsTemplate?.tags?.find((tag) => tag.id === TermsType.ADVANCED);
    if (isAdvanced) {
        return TermsType.ADVANCED;
    }

    return TermsType.BASIC;
};

// Advance Purchase Rule Helpers

/**
 * Get initial Purchase Rule payload data.
 * Returns `purchase rule payload with default actions`
 */
const initializePurchaseRuleFromAdvancedTermsTemplate = (
    seller: Seller,
    lineItemTemplate: LineItemTemplate,
    partnerDemographicTerm: PartnerDemographicTerm,
    termsTemplate?: PurchaseRuleLink,
    termsTemplatePurchaseRule?: PurchaseRule,
): PurchaseRule => {
    // If existing termst template is also advanced, we can use the existing purchase rule id, else we need to
    // create a new rule, other wise it will create multiple rules with same id.
    const existingTermsTemplateType = getTermsType(termsTemplate);
    const shouldCreateAsNewRule = existingTermsTemplateType === TermsType.BASIC;
    let name = `Purchase Rule for ${lineItemTemplate.name}, product ${partnerDemographicTerm?.product.name}`;
    name = shouldCreateAsNewRule ? name : termsTemplatePurchaseRule?.name ?? name;

    return {
        id: shouldCreateAsNewRule ? undefined : termsTemplatePurchaseRule?.id,
        name,
        seller: { id: seller.id },
        filter: {
            object: PurchaseRuleFilterObject.IN_FILTER,
            operator: "in",
            operand: [partnerDemographicTerm?.product.id],
            field: "product.id",
        },
        actions: [
            {
                template: { id: lineItemTemplate.id },
                object: PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION,
            },
        ],
        tags: [{ id: "Affiliate" }, { id: lineItemTemplate.type }],
    };
};

/**
 * Generate Purchase Rule payload data from PartnerDemographicTerm for advance terms.
 */
export const getAdvancedPurchaseRulePayload = (
    seller: Seller,
    lineItemTemplate: LineItemTemplate,
    partnerDemographicTerm: PartnerDemographicTerm,
    termsTemplatePurchaseRules: PurchaseRule[],
    termsTemplate?: PurchaseRuleLink,
): PurchaseRule => {
    const productPurchaseRules = filterPurchaseRulesByProductId(
        termsTemplatePurchaseRules,
        partnerDemographicTerm?.product.id,
    );
    const discountPurchaseRule = getPurchaseRulesByTag(LineItemType.AFFILIATE_DISCOUNT, productPurchaseRules)[0];

    const commissionPurchaseRule = getPurchaseRulesByTag(LineItemType.AFFILIATE_COMMISSION, productPurchaseRules)[0];

    // Get initial purchase rule payload
    const purchaseRulePayload = initializePurchaseRuleFromAdvancedTermsTemplate(
        seller,
        lineItemTemplate,
        partnerDemographicTerm,
        termsTemplate,
        lineItemTemplate.type === LineItemType.AFFILIATE_COMMISSION ? commissionPurchaseRule : discountPurchaseRule,
    );
    // update purchase rule action payload depends on LineItemType, as both commission and discount has dif. actions
    purchaseRulePayload.actions =
        lineItemTemplate.type === LineItemType.AFFILIATE_COMMISSION
            ? generateAdvancedPurchaseRuleCommissionActionItems(
                  partnerDemographicTerm,
                  lineItemTemplate,
                  termsTemplate,
                  commissionPurchaseRule,
              )
            : generateAdvancedPurchaseRuleDiscountActionItems(
                  partnerDemographicTerm,
                  lineItemTemplate,
                  termsTemplate,
                  discountPurchaseRule,
              );

    return purchaseRulePayload;
};

/**
 * Generate Purchase Rule Discount actions payload data.
 * Returns actions generated for Discount
 */
const generateAdvancedPurchaseRuleDiscountActionItems = (
    partnerDemographicTerm: PartnerDemographicTerm,
    lineItemTemplate: LineItemTemplate,
    termsTemplate?: PurchaseRuleLink,
    termsTemplatePurchaseRule?: PurchaseRule,
): PurchaseRuleAction[] => {
    const shouldCreateAsNewRule = getTermsType(termsTemplate) === TermsType.BASIC;
    const purchaseRuleActionItems: PurchaseRuleAction[] =
        termsTemplatePurchaseRule?.actions && !shouldCreateAsNewRule
            ? addRemoveFlagToActions(termsTemplatePurchaseRule?.actions)
            : [];

    for (const demographicPrice of partnerDemographicTerm.partnerDemographicPrices) {
        const isDiscountAdjustmentOperation =
            demographicPrice.discount.adjustmentOperation === AdjustmentOperation.DISCOUNT;

        /**
         * For Discount operation, we will update the partner discount line item instead of demographic line item
         */
        const applicableTemplateId = isDiscountAdjustmentOperation ? lineItemTemplate.id : demographicPrice.template.id;
        const purchaseRuleActionItem: PurchaseRuleAction = {
            object: isDiscountAdjustmentOperation
                ? PurchaseRuleActionObject.UPSERT_LINE_ITEM_ACTION
                : PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
        };

        if (isDiscountAdjustmentOperation) {
            /**
             * we will need shortcut to store the demographic id for which this
             * action is applicable.
             */
            purchaseRuleActionItem.shortcut = generateDiscountShortcut(demographicPrice.discount);
        } else {
            purchaseRuleActionItem.modifiers = [generateDiscountModifier(demographicPrice.discount, "price")];
            purchaseRuleActionItem.filter = getEqualFilter(applicableTemplateId, "template.id");
        }

        purchaseRuleActionItem.template = { id: applicableTemplateId };
        // find is action already exists
        const existingActionIndex = purchaseRuleActionItem.id
            ? findIndex(purchaseRuleActionItems, { id: purchaseRuleActionItem.id })
            : -1;

        if (existingActionIndex >= 0) {
            // update newly generated purchase Rule Actions on existing action index, so it wont have _remove flag
            purchaseRuleActionItems[existingActionIndex] = purchaseRuleActionItem;
        } else {
            purchaseRuleActionItems.push(purchaseRuleActionItem);
        }
    }

    return purchaseRuleActionItems;
};

/**
 * Generate Purchase Rule Discount Modifier data.
 */
const generateDiscountModifier = (demographicPrice: DemographicPrice, field: string): PurchaseRuleModifier => {
    const adjustmentAmount = demographicPrice.adjustmentAmount ?? 0;
    if (demographicPrice.adjustmentOperation === AdjustmentOperation.FINAL) {
        return {
            field,
            operation: PurchaseRuleModifierOperation.SET,
            aggregator: getReturnValueAggregator(adjustmentAmount),
        };
    }

    const isMarkDown = demographicPrice.adjustmentOperation === AdjustmentOperation.MARKDOWN;
    const isDiscount = demographicPrice.adjustmentOperation === AdjustmentOperation.DISCOUNT;

    const modifier: PurchaseRuleModifier = {
        field,
        operation: PurchaseRuleModifierOperation.INC,
        aggregator: getReturnValueAggregator((isMarkDown || isDiscount ? -1 : 1) * adjustmentAmount),
    };

    if (demographicPrice.adjustmentType === AdjustmentType.PERCENT) {
        modifier.aggregator = {
            object: AggregatorObject.PERCENT_AGGREGATOR,
            percentValue: (isMarkDown || isDiscount ? -1 : 1) * adjustmentAmount,
            operand: getReduceAggregator(demographicPrice.template.id, "lineItems", "template.id", "price"),
        };
    }

    return modifier;
};

const generateDiscountShortcut = (demographicPrice: DemographicPrice): PurchaseRuleShortcut => {
    const { adjustmentType, adjustmentAmount, adjustmentOperation } = demographicPrice;
    const isMarkDown = adjustmentOperation === AdjustmentOperation.MARKDOWN;
    const isDiscount = adjustmentOperation === AdjustmentOperation.DISCOUNT;

    const type =
        adjustmentType === AdjustmentType.PERCENT
            ? PurchaseRuleShortcutType.PERCENT
            : PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT;

    const shortcut: PurchaseRuleShortcut = {
        amount: (isMarkDown || isDiscount ? -1 : 1) * (adjustmentAmount ?? 0),
        options: {
            templateIds: [demographicPrice.template.id],
        },
        type: type,
        operation: PurchaseRuleModifierOperation.INC,
    };

    if (adjustmentOperation === AdjustmentOperation.FINAL) {
        shortcut.type = PurchaseRuleShortcutType.ABSOLUTE_FINAL_PRICE;
        shortcut.operation = PurchaseRuleModifierOperation.SET;
    }

    return shortcut;
};

/**
 * Generate Purchase Rule Commission actions payload data from PartnerDemographicTerm.
 */
const generateAdvancedPurchaseRuleCommissionActionItems = (
    partnerDemographicTerm: PartnerDemographicTerm,
    lineItemTemplate: LineItemTemplate,
    termsTemplate?: PurchaseRuleLink,
    termsTemplatePurchaseRule?: PurchaseRule,
): PurchaseRuleAction[] => {
    // Removing existing Actions by passing _remove:true to API.
    const shouldCreateAsNewRule = getTermsType(termsTemplate) === TermsType.BASIC;
    const purchaseRuleActionItems: PurchaseRuleAction[] =
        termsTemplatePurchaseRule?.actions && !shouldCreateAsNewRule
            ? addRemoveFlagToActions(termsTemplatePurchaseRule?.actions)
            : [];

    for (const [index, demographicPrice] of partnerDemographicTerm.partnerDemographicPrices.entries()) {
        const purchaseRuleActionItem: PurchaseRuleAction = {
            ...(demographicPrice.commission.actionId && { id: demographicPrice.commission.actionId }),
            object:
                //  INSERT_LINE_ITEM_ACTION only on first index
                index === 0
                    ? PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION
                    : PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
            shortcut: {
                type:
                    demographicPrice.commission.adjustmentType === AdjustmentType.PERCENT
                        ? PurchaseRuleShortcutType.PERCENT
                        : PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT,
                amount: demographicPrice.commission.adjustmentAmount ?? 0,
                options: { templateIds: [demographicPrice.template.id] },
            },
        };

        if (index === 0) {
            // template need for only INSERT_LINE_ITEM_ACTION
            purchaseRuleActionItem.template = {
                id: lineItemTemplate.id,
            };
        }

        if (index > 0) {
            // update filter, and shortcut for UPDATE_LINE_ITEM_ACTION
            purchaseRuleActionItem.filter = getEqualFilter(lineItemTemplate.id, "template.id");
            purchaseRuleActionItem.shortcut!.field = "amount";
            purchaseRuleActionItem.shortcut!.operation = PurchaseRuleModifierOperation.INC;
        }

        const existingActionIndex = purchaseRuleActionItem.id
            ? findIndex(purchaseRuleActionItems, { id: purchaseRuleActionItem.id })
            : -1;

        if (existingActionIndex >= 0) {
            purchaseRuleActionItems[existingActionIndex] = purchaseRuleActionItem;
        } else {
            purchaseRuleActionItems.push(purchaseRuleActionItem);
        }
    }

    return purchaseRuleActionItems;
};

/**
 * Add remove flag to true on all PurchaseRuleAction
 */
const addRemoveFlagToActions = (actions: PurchaseRuleAction[] = []): PurchaseRuleAction[] => {
    return actions
        .filter((action) => action.id)
        .map((action) => {
            action._remove = true;
            return action;
        });
};

//  Generate PartnerDemographicTerms form values from Purchase Rules
export const generatePartnerDemographicTerms = (
    purchaseRules: PurchaseRule[] | null,
    experiences: Experience[],
): PartnerDemographicTerm[] => {
    if (!purchaseRules) {
        return [];
    }

    purchaseRules = purchaseRules?.filter((purchaseRule) => {
        const tags = getTagIdsFromPurchaseRule(purchaseRule);
        return !tags.includes(TagId.AFFILIATE_OVERRIDE);
    });

    const discountPurchaseRule = getPurchaseRulesByTag(LineItemType.AFFILIATE_DISCOUNT, purchaseRules);
    // get list of products from all Purchase rules, used discountPurchaseRule
    // as each Terms has same number of purchase rules for either discount or commission
    const selectedProductIds: string[] = getProductIdsFromPurchaseRules(discountPurchaseRule);
    const partnerDemographicTerms: PartnerDemographicTerm[] = [];

    for (const productId of selectedProductIds) {
        // filtered purchase rules of single product
        const productPurchaseRules = filterPurchaseRulesByProductId(purchaseRules, productId);
        // filtered discount purchase rules of single product
        const discountPurchaseRule = getPurchaseRulesByTag(LineItemType.AFFILIATE_DISCOUNT, productPurchaseRules)[0];
        // filtered commission purchase rules of single product
        const commissionPurchaseRule = getPurchaseRulesByTag(
            LineItemType.AFFILIATE_COMMISSION,
            productPurchaseRules,
        )[0];

        const experience = experiences.find((experience) => experience.id === productId);

        const partnerDemographicTerm = {
            product: { id: productId },
            partnerDemographicPrices: generatePartnerDemographicTerm(
                experience,
                discountPurchaseRule,
                commissionPurchaseRule,
            ),
        };

        partnerDemographicTerms.push(partnerDemographicTerm);
    }

    return partnerDemographicTerms;
};

/**
 * Generate PartnerDemographicTerms from values from discount and commission PurchaseRules
 */
export const generatePartnerDemographicTerm = (
    experience?: Experience,
    discountPurchaseRule?: PurchaseRule | null,
    commissionPurchaseRule?: PurchaseRule | null,
): PartnerDemographicPrice[] => {
    const partnerDemographicPrices: PartnerDemographicPrice[] = [];
    const demographicCatalogItems: CatalogItem[] = experience ? getDemographicCatalog(experience) : [];

    if (discountPurchaseRule) {
        for (const action of discountPurchaseRule.actions) {
            const modifier = action.modifiers ? action.modifiers[0] : null;
            const shortcut = action.shortcut ? action.shortcut : null;
            let demographicId = "";
            let shouldUseShortcut = false;

            if (action.filter?.object === PurchaseRuleFilterObject.EQUALS_FILTER) {
                demographicId = action.filter.operand;
            }

            if (action.shortcut) {
                shouldUseShortcut = true;
                demographicId = action.shortcut.options?.templateIds?.[0] ?? demographicId;
            }

            const catalogItem = getCatalogItemByTemplateId(demographicCatalogItems, demographicId);

            // get initial discount DemographicPrice and update
            const discount: DemographicPrice = getInitialPartnerDemographicPrice(demographicId, catalogItem);

            discount.actionId = action.id;

            const { adjustmentAmount, adjustmentType, adjustmentOperation } =
                shouldUseShortcut && shortcut
                    ? getValuesFromActionShortcut(demographicId, action)
                    : getDiscountModifers(modifier);

            discount.adjustmentAmount = adjustmentAmount;
            discount.adjustmentType = adjustmentType;
            discount.adjustmentOperation = adjustmentOperation;

            // get initial commission DemographicPrice and update
            const commission: DemographicPrice = getInitialPartnerDemographicPrice(demographicId, catalogItem);
            const commissionModifiers = getCommissionModifiers(demographicId, commissionPurchaseRule?.actions);
            commission.actionId = commissionModifiers.commisionActionId;
            commission.adjustmentType = commissionModifiers.commisionAlgorithm;
            commission.adjustmentAmount = commissionModifiers.commisionAmount;

            partnerDemographicPrices.push({
                template: { id: demographicId },
                commission,
                discount,
            });
        }
    }

    return partnerDemographicPrices;
};

const getValuesFromActionShortcut = (demographicId: ID, action: PurchaseRuleAction) => {
    const shortcut = action.shortcut!;
    // let demographicId = shortcut?.options?.templateIds?.[0] ?? "";
    let operation = AdjustmentOperation.FINAL;
    let type = AdjustmentType.ABSOLUTE;
    const amount = shortcut?.amount ?? 0;
    const isDiscount = demographicId !== action.template?.id;

    switch (shortcut.type) {
        case PurchaseRuleShortcutType.PERCENT:
            operation = amount >= 0 ? AdjustmentOperation.MARKUP : AdjustmentOperation.MARKDOWN;
            type = AdjustmentType.PERCENT;
            break;
        case PurchaseRuleShortcutType.ABSOLUTE_FINAL_PRICE:
            operation = AdjustmentOperation.FINAL;
            type = AdjustmentType.ABSOLUTE;
            break;
        default:
            operation = amount >= 0 ? AdjustmentOperation.MARKUP : AdjustmentOperation.MARKDOWN;
            type = AdjustmentType.ABSOLUTE;
            break;
    }

    return {
        adjustmentOperation: isDiscount ? AdjustmentOperation.DISCOUNT : operation,
        adjustmentAmount: Math.abs(shortcut?.amount ?? 0),
        adjustmentType: type,
    };
};

interface CommissionModifers {
    commisionActionId?: ID;
    commisionAlgorithm: AdjustmentType;
    commisionAmount: number;
}

/**
 * Generate Commission Modifiers from PurchaseRuleAction
 */
export const getCommissionModifiers = (demographicId?: ID, actions?: PurchaseRuleAction[]): CommissionModifers => {
    const commissionModifers: CommissionModifers = {
        commisionAlgorithm: AdjustmentType.ABSOLUTE,
        commisionAmount: 0,
    };

    // Find commission action for given demographic id
    const commissionAction = actions?.find((action) => {
        const options = action.shortcut?.options;
        if (!options) {
            return false;
        }

        if ("templateIds" in options) {
            return options.templateIds?.some((id: ID) => demographicId?.includes(id));
        }

        return false;
    });

    // if there is an commission action for demographic id, update initial commission Modifers
    commissionModifers.commisionActionId = commissionAction?.id;

    if (commissionAction?.shortcut!.type === PurchaseRuleShortcutType.PERCENT) {
        commissionModifers.commisionAlgorithm = AdjustmentType.PERCENT;
    }

    commissionModifers.commisionAmount = commissionAction?.shortcut!.amount ?? 0;

    return commissionModifers;
};

interface DiscountModifers {
    adjustmentOperation: AdjustmentOperation;
    adjustmentAmount: number;
    adjustmentType: AdjustmentType;
}

/**
 * Generate Discount Modifiers from PurchaseRuleAction
 */
const getDiscountModifers = (modifier: PurchaseRuleModifier | null): DiscountModifers => {
    const discountModifers: DiscountModifers = {
        adjustmentOperation: AdjustmentOperation.FINAL,
        adjustmentAmount: Math.abs(modifier?.aggregator.operand as number),
        adjustmentType: AdjustmentType.ABSOLUTE,
    };

    // update initial commission Modifers
    if (modifier?.operation !== PurchaseRuleModifierOperation.SET) {
        const isPercentAggregator = modifier?.aggregator.object === AggregatorObject.PERCENT_AGGREGATOR;

        const adjustmentAmount = isPercentAggregator
            ? modifier.aggregator.percentValue ?? 0
            : modifier?.aggregator.operand ?? 0;

        discountModifers.adjustmentOperation =
            isNumber(adjustmentAmount) && adjustmentAmount < 0
                ? AdjustmentOperation.MARKDOWN
                : AdjustmentOperation.MARKUP;
        discountModifers.adjustmentAmount = Math.abs(adjustmentAmount as number);
        discountModifers.adjustmentType = isPercentAggregator ? AdjustmentType.PERCENT : AdjustmentType.ABSOLUTE;
    }

    return discountModifers;
};

/**
 * Generate Initial Partner Demographic Prices values for single catalogItem
 */
export const getInitialPartnerDemographicPrices = (
    catalogItem: CatalogItem,
    partnerDemographicPrice?: PartnerDemographicPrice,
): PartnerDemographicPrice => {
    // Dempgraphics catalog items always have template.id
    const demographicId = catalogItem.template!.id;
    const termTemplate: PartnerDemographicPrice = {
        template: { id: demographicId },
        commission: getInitialPartnerDemographicPrice(demographicId, catalogItem),
        discount: getInitialPartnerDemographicPrice(demographicId, catalogItem),
        ...partnerDemographicPrice,
    };

    return termTemplate;
};

/**
 * Generate Initial Partner Demographic Price values
 */
export const getInitialPartnerDemographicPrice = (demographicId: ID, catalogItem?: CatalogItem): DemographicPrice => {
    return {
        template: { id: demographicId },
        adjustmentType: AdjustmentType.ABSOLUTE,
        adjustmentOperation: AdjustmentOperation.MARKUP,
        adjustmentAmount: 0,
        catalogItem: catalogItem,
    };
};
